import { useMemo, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  ChevronRight,
  ExternalLink,
  GitCommitVertical,
  UserCheck,
  ClipboardList
} from 'lucide-react';
import { Link } from '@tanstack/react-router';
import {
  Badge,
  Button,
  UserCard,
  createColumnHeader,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  type DataTableColumnDef
} from '@bliks-io/ui';
import { useAuthStore } from '@/stores/auth-store';
import { PROCESS_STATUS_COLOR_SCHEME, processStatusLabel } from '@/lib/process-utils';
import type { RecordingDashboardFlatStep, RecordingDashboardSurvey } from '@bliks-io/contracts';
import { toUserCardProps } from '../types';

// ── Process row type ────────────────────────────────────────────────

export interface InRecordingProcessRow {
  _id: string;
  name: string;
  owner: RecordingDashboardFlatStep['owner'];
  processStatus: RecordingDashboardFlatStep['processStatus'];
  isOverdue: boolean;
  overdueCount: number;
  completedStepCount: number;
  totalStepCount: number;
  openAssignmentCount: number;
  inRecordingStepCount: number;
  pendingSurveyCount: number;
  steps: RecordingDashboardFlatStep[];
  surveys: RecordingDashboardSurvey[];
}

// ── Derive grouped process rows from flat data ──────────────────────

export function deriveProcessRows(
  steps: RecordingDashboardFlatStep[],
  surveys: RecordingDashboardSurvey[]
): InRecordingProcessRow[] {
  const map = new Map<string, InRecordingProcessRow>();
  const now = Date.now();

  for (const step of steps) {
    let row = map.get(step.processId);
    if (!row) {
      row = {
        _id: step.processId,
        name: step.processName,
        owner: step.owner,
        processStatus: step.processStatus,
        isOverdue: false,
        overdueCount: 0,
        completedStepCount: 0,
        totalStepCount: 0,
        openAssignmentCount: 0,
        inRecordingStepCount: 0,
        pendingSurveyCount: 0,
        steps: [],
        surveys: []
      };
      map.set(step.processId, row);
    }
    row.steps.push(step);
    row.totalStepCount++;
    if (step.isCompleted) row.completedStepCount++;
    else if (step.assignmentId && !step.recordingId) row.openAssignmentCount++;
    else if (step.recordingId) row.inRecordingStepCount++;
    if (step.isOverdue && !step.isCompleted) row.overdueCount++;
    if (step.isOverdue && !step.isCompleted) row.isOverdue = true;
  }

  for (const survey of surveys) {
    const row = map.get(survey.processId);
    if (!row) continue;
    row.surveys.push(survey);
    if (survey.status === 'pending') {
      row.pendingSurveyCount++;
      if (survey.dueDate && survey.dueDate < now) row.overdueCount++;
    }
  }

  return Array.from(map.values());
}

// ── Hook ────────────────────────────────────────────────────────────

export function useInRecordingProcessColumns() {
  const { t } = useTranslation();
  const slug = useAuthStore().slug();

  return useMemo(
    (): DataTableColumnDef<InRecordingProcessRow>[] => [
      {
        // Expand toggle. The whole row also toggles (DataTable onRowClick);
        // this button stops propagation so a click here toggles exactly once,
        // while keeping a keyboard-focusable control for the primary action.
        id: 'expander',
        meta: { fitContent: true },
        header: () => null,
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="icon"
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
              row.toggleExpanded();
            }}
            aria-expanded={row.getIsExpanded()}
            aria-label={t('recordingDashboard.actions.toggleDetails')}
            className="h-6 w-6"
          >
            <ChevronRight
              className={`h-4 w-4 transition-transform ${row.getIsExpanded() ? 'rotate-90' : ''}`}
            />
          </Button>
        ),
        enableSorting: false,
        enableHiding: false
      },
      {
        accessorKey: 'name',
        header: createColumnHeader(t('recordingDashboard.columns.process')),
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">{row.original.name}</span>
            {row.original.processStatus === 'recording_complete' && (
              <Badge
                colorScheme={PROCESS_STATUS_COLOR_SCHEME[row.original.processStatus]}
                size="sm"
              >
                {processStatusLabel(row.original.processStatus, t)}
              </Badge>
            )}
          </div>
        ),
        enableSorting: true
      },
      {
        accessorKey: 'owner',
        header: createColumnHeader(t('recordingDashboard.columns.owner')),
        cell: ({ row }) => (
          <UserCard user={toUserCardProps(row.original.owner)} variant="with-role" size="sm" />
        ),
        accessorFn: (row) => `${row.owner.firstName} ${row.owner.lastName}`,
        enableSorting: true
      },
      {
        id: 'status',
        header: createColumnHeader(t('recordingDashboard.columns.statusRecording')),
        accessorFn: (row) => row.completedStepCount / (row.totalStepCount || 1),
        cell: ({ row }) => {
          const {
            completedStepCount,
            totalStepCount,
            overdueCount,
            openAssignmentCount: openAssignments,
            inRecordingStepCount: inRecordingSteps,
            pendingSurveyCount: pendingSurveys
          } = row.original;
          const pct = totalStepCount ? Math.round((completedStepCount / totalStepCount) * 100) : 0;

          return (
            <div className="space-y-0.5">
              <div className="space-y-0.5 text-xs text-muted-foreground">
                {overdueCount > 0 && (
                  <div className="flex items-center gap-1.5 text-danger">
                    <AlertTriangle className="h-3 w-3" />
                    <span>
                      {t('recordingDashboard.status.overdue', {
                        count: overdueCount
                      })}
                    </span>
                  </div>
                )}
                {openAssignments > 0 && (
                  <div className="flex items-center gap-1.5">
                    <UserCheck className="h-3 w-3 text-warning" />
                    <span>
                      {t('recordingDashboard.status.openAssignments', {
                        count: openAssignments
                      })}
                    </span>
                  </div>
                )}
                {inRecordingSteps > 0 && (
                  <div className="flex items-center gap-1.5">
                    <GitCommitVertical className="h-3 w-3 text-positive" />
                    <span>
                      {t('recordingDashboard.status.inRecordingSteps', {
                        count: inRecordingSteps
                      })}
                    </span>
                  </div>
                )}
                {pendingSurveys > 0 && (
                  <div className="flex items-center gap-1.5">
                    <ClipboardList className="h-3 w-3 text-info" />
                    <span>
                      {t('recordingDashboard.status.pendingSurveys', {
                        count: pendingSurveys
                      })}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 mt-1">
                <div className="h-1.5 w-16 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-positive-solid"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-default text-xs text-muted-foreground">
                        {t('recordingDashboard.status.stepsComplete', {
                          completed: completedStepCount,
                          total: totalStepCount
                        })}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>{t('recordingDashboard.status.stepsTooltip')}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          );
        },
        enableSorting: true
      },
      {
        // Explicit navigation to the full process overview. The row click is
        // reserved for expanding/clarifying assignments, so jumping to the
        // process needs its own affordance.
        id: 'open',
        meta: { fitContent: true },
        header: () => null,
        cell: ({ row }) => (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  asChild
                  className="h-6 w-6 text-muted-foreground"
                >
                  <Link
                    to="/$slug/processes/all-processes/$processId"
                    params={{ slug: slug!, processId: row.original._id }}
                    onClick={(e: MouseEvent) => e.stopPropagation()}
                    aria-label={t('recordingDashboard.actions.openProcess')}
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Link>
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('recordingDashboard.actions.openProcess')}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ),
        enableSorting: false,
        enableHiding: false
      }
    ],
    [t, slug]
  );
}
